import { Card, Empty, StatTile, StatusPill } from '../components/ui';
import { FirewallSection } from '../components/connectiq/FirewallSection';
import { FunnelToggle } from '../components/connectiq/FunnelToggle';
import type { Capabilities, Funnel, FirewallOverview } from '../components/connectiq/types';
import { useApi } from '../lib/useApi';

interface Overview {
  ca: { name: string; fqdn: string; provisioner: string | null; status: string; lastCheckedAt: string } | null;
  jump: { name: string; hostname: string; status: string; lastCheckedAt: string } | null;
  deployFunnelEnabled: boolean;
  firewall: { hosts: Array<{ id: string; name: string; hostname: string; rules: number; rulesReadable: boolean | null; caTrustInstalled: boolean | null }> };
}

function day(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—';
}

export default function ConnectIQ() {
  const overview = useApi<Overview>('/connectiq');
  const funnel = useApi<Funnel>('/connectiq/deploy-funnel');
  const firewall = useApi<FirewallOverview>('/connectiq/firewall');
  const caps = useApi<Capabilities>('/capabilities');

  if (overview.loading && !overview.data) return <Empty>Loading ConnectIQ…</Empty>;
  if (overview.error && !overview.data) return <Empty>Could not load ConnectIQ: {overview.error}</Empty>;
  if (!overview.data) return null;

  const reloadAll = () => {
    overview.reload();
    funnel.reload();
    firewall.reload();
  };
  const jumpTrust = overview.data.firewall.hosts.find((h) => h.caTrustInstalled !== null)?.caTrustInstalled ?? null;
  const capsData: Capabilities = caps.data ?? { canTouchProduction: false, canManageFirewall: false, canChangeFunnel: false };

  return (
    <>
      <div className="page-head">
        <h1>ConnectIQ</h1>
      </div>

      <div className="tiles">
        <StatTile
          label="SSH certificate authority"
          value={overview.data.ca ? overview.data.ca.name : 'Not set up'}
          sub={overview.data.ca ? <StatusPill status={overview.data.ca.status} /> : 'No CA row yet'}
        />
        <StatTile
          label="Jump host"
          value={overview.data.jump ? overview.data.jump.name : 'Not set up'}
          sub={overview.data.jump ? <StatusPill status={overview.data.jump.status} /> : 'No jump host row yet'}
        />
        <StatTile label="SSH funnel" value={overview.data.deployFunnelEnabled ? 'On' : 'Off'} sub="Off by default" />
        <StatTile label="Systems under firewall management" value={overview.data.firewall.hosts.length} sub="cert01 and jump01" />
      </div>

      <div className="grid-cards">
        <Card title="Certificate authority">
          {overview.data.ca ? (
            <dl className="kv">
              <dt>FQDN</dt>
              <dd className="mono">{overview.data.ca.fqdn}</dd>
              <dt>Provisioner</dt>
              <dd>{overview.data.ca.provisioner ?? 'Not configured'}</dd>
              <dt>Last checked</dt>
              <dd>{day(overview.data.ca.lastCheckedAt)}</dd>
            </dl>
          ) : (
            <Empty>cert01 is not registered yet.</Empty>
          )}
        </Card>

        <Card title="Jump host">
          {overview.data.jump ? (
            <dl className="kv">
              <dt>FQDN</dt>
              <dd className="mono">{overview.data.jump.hostname}</dd>
              <dt>Trusts the SSH CA</dt>
              <dd>{jumpTrust === true ? 'Yes, read from the host' : jumpTrust === false ? 'No' : 'Not read yet'}</dd>
            </dl>
          ) : (
            <Empty>jump01 is not registered yet.</Empty>
          )}
        </Card>

        <Card title="SSH funnel">
          {funnel.data ? (
            <FunnelToggle
              funnel={funnel.data}
              canTurnOn={capsData.canChangeFunnel}
              canTurnOff={capsData.canChangeFunnel}
              onChanged={reloadAll}
            />
          ) : (
            <Empty>{funnel.error ?? 'Loading…'}</Empty>
          )}
        </Card>
      </div>

      {firewall.data ? (
        <FirewallSection overview={firewall.data} caps={capsData} onChanged={reloadAll} />
      ) : (
        <Empty>{firewall.error ?? 'Loading firewall…'}</Empty>
      )}
    </>
  );
}
